import { Fragment, type ReactNode } from "react";

import { classNames } from "./Primitives";
import {
  cardStyles,
  layoutStyles,
  surfaceStyles,
  type CardPresentation,
  type SurfaceCollisionPolicy,
  type SurfaceTone,
  resolveCardSurface,
} from "./style-config";

export function PageTemplate({
  children,
  tone = "default",
  className,
}: {
  children: ReactNode;
  tone?: SurfaceTone;
  className?: string;
}) {
  return (
    <main className={classNames(layoutStyles.page, surfaceStyles[tone], className)}>
      <div className={layoutStyles.pageContent}>{children}</div>
    </main>
  );
}

export function SectionHeading({
  eyebrow,
  title,
  id,
  tone = "default",
  className,
}: {
  eyebrow?: string;
  title: string;
  id?: string;
  tone?: "default" | "light";
  className?: string;
}) {
  return (
    <header className={className}>
      {eyebrow ? (
        <p
          className={classNames(
            "font-tagada text-fluid-base",
            tone === "light" ? "text-millet" : "text-amaranth",
          )}
        >
          {eyebrow}
        </p>
      ) : null}
      <h2
        id={id}
        className={classNames(
          "font-neueheitz text-fluid-lg leading-[1.05]",
          eyebrow && "pt-3",
        )}
      >
        {title}
      </h2>
    </header>
  );
}

export function SectionTemplate({
  children,
  eyebrow,
  title,
  headingId,
  tone = "default",
  spacing = "inset",
  className,
  contentClassName,
}: {
  children: ReactNode;
  eyebrow?: string;
  title?: string;
  headingId?: string;
  tone?: SurfaceTone;
  spacing?: "page" | "following" | "inset" | "none";
  className?: string;
  contentClassName?: string;
}) {
  const spacingClass = {
    page: layoutStyles.section,
    following: layoutStyles.followingSection,
    inset: layoutStyles.sectionInset,
    none: "",
  }[spacing];

  return (
    <section
      aria-labelledby={headingId}
      className={classNames(surfaceStyles[tone], spacingClass, className)}
    >
      <div className={classNames(spacing === "inset" && layoutStyles.contentMax, contentClassName)}>
        {title ? (
          <SectionHeading
            eyebrow={eyebrow}
            title={title}
            id={headingId}
            tone={tone === "accent" || tone === "dark" || tone === "earth" || tone === "clay" ? "light" : "default"}
          />
        ) : null}
        {children}
      </div>
    </section>
  );
}

export function CardTemplate({
  media,
  children,
  footer,
  tone = "default",
  presentation = "catalog",
  bordered = false,
  className,
  mediaClassName,
  bodyClassName,
}: {
  media: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  tone?: SurfaceTone;
  presentation?: CardPresentation;
  bordered?: boolean;
  className?: string;
  mediaClassName?: string;
  bodyClassName?: string;
}) {
  return (
    <article
      className={classNames(
        cardStyles.frame,
        bordered && cardStyles.bordered,
        cardStyles.width[presentation],
        surfaceStyles[tone],
        className,
      )}
    >
      <div
        className={classNames(
          "relative overflow-hidden",
          cardStyles.media[presentation],
          mediaClassName,
        )}
      >
        {media}
      </div>
      <div className={classNames(cardStyles.body[presentation], bodyClassName)}>
        {children}
        {footer}
      </div>
    </article>
  );
}

export function ContentGrid({
  children,
  variant = "catalog",
  className,
}: {
  children: ReactNode;
  variant?: "catalog" | "related" | "relatedProducts" | "wall";
  className?: string;
}) {
  const gridClass = {
    catalog: layoutStyles.catalogGrid,
    related: layoutStyles.relatedGrid,
    relatedProducts: layoutStyles.relatedProductGrid,
    wall: layoutStyles.wallGrid,
  }[variant];

  return <div className={classNames(gridClass, className)}>{children}</div>;
}

export function CardCollection<T>({
  items,
  getKey,
  renderCard,
  parentTone = "default",
  requestedTone,
  collisionPolicy = "auto",
  variant = "catalog",
  className,
}: {
  items: readonly T[];
  getKey: (item: T) => string;
  renderCard: (
    item: T,
    index: number,
    surface: ReturnType<typeof resolveCardSurface>,
  ) => ReactNode;
  parentTone?: SurfaceTone;
  requestedTone?: SurfaceTone;
  collisionPolicy?: SurfaceCollisionPolicy;
  variant?: "catalog" | "related" | "relatedProducts" | "wall";
  className?: string;
}) {
  return (
    <ContentGrid variant={variant} className={className}>
      {items.map((item, index) => (
        <Fragment key={getKey(item)}>
          {renderCard(
            item,
            index,
            resolveCardSurface({
              parentTone,
              requestedTone,
              collisionPolicy,
              index,
            }),
          )}
        </Fragment>
      ))}
    </ContentGrid>
  );
}

export function DetailPageTemplate({
  heroCopy,
  heroMedia,
  body,
  related,
  heroTone = "accent",
  heroCopyTone,
  heroMediaPosition = "right",
  className,
}: {
  heroCopy: ReactNode;
  heroMedia: ReactNode;
  body: ReactNode;
  related?: ReactNode;
  heroTone?: SurfaceTone;
  heroCopyTone?: SurfaceTone;
  heroMediaPosition?: "left" | "right";
  className?: string;
}) {
  const copy = (
    <div
      className={classNames(
        "flex flex-col justify-center px-8 py-14 lg:px-16 lg:py-20",
        surfaceStyles[heroCopyTone ?? heroTone],
      )}
    >
      {heroCopy}
    </div>
  );

  return (
    <main
      className={classNames(
        "min-h-screen",
        surfaceStyles.default,
        className,
      )}
    >
      <article>
        <header
          className={classNames(
            "grid lg:grid-cols-2",
            surfaceStyles[heroTone],
          )}
        >
          {heroMediaPosition === "left" ? heroMedia : copy}
          {heroMediaPosition === "left" ? copy : heroMedia}
        </header>
        {body}
      </article>
      {related}
    </main>
  );
}
